import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Divider,
  Stack,
  Box,
  Spacer,
} from "digiwash-components";
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field";
import { numbersFormatter } from "../../helpers/number";
import { Platform, StyleSheet, TouchableOpacity } from "react-native";
import Icon from "../Icon";
import { formatNumber, isNumeric } from "digiwash-core";

import userSelector from "../../ducks/user/selectors";
import { useSelector } from "react-redux";
import { formatTime } from "../../helpers/format-helper";
import dayjs from "dayjs";
import useLogin from "../../hooks/useLogin";
import theme from "../../configs/theme-config";
import { color as colorUtil } from "@sloth-system/styled-utils";
import Loading from "../Loading";
import WebCodeField from "../WebCodeField";

let webInputProps =
  Platform.OS === "web"
    ? {
        type: "text",
        id: "single-factor-code-text-field",
        autoComplete: "one-time-code",
      }
    : {};

const styles = StyleSheet.create({
  root: { flex: 1, padding: 20 },
  title: { textAlign: "center" },
  cell: {
    width: 48,
    height: 48,
    lineHeight: 48,
    fontSize: 16,
    textAlign: "center",
    borderBottomWidth: 2,
    borderBottomColor: colorUtil("neutrals.300")({ theme: theme }),
  },
  focusCell: {
    borderBottomWidth: 2,
    borderBottomColor: colorUtil("common.black")({ theme: theme }),
  },
  textInputStyle: {
    height: "100%",
    opacity: 0.1,
    width: 0.1,
    borderWidth: 0,
    overflow: "hidden",
  },
});

interface PropTypes {
  handleVerify: (code) => void;
  loading: boolean;
  back: () => void;
}

function VerifyStep(props: PropTypes) {
  let [code, setCode] = useState("");
  let { meta } = useSelector(userSelector.login);
  let [visibleResendCodeButton, setVisibleResendcodeButton] = useState(true);

  let [expireTime, setExpireTime] = useState(undefined);
  let onSubmit = (code) => props.handleVerify(numbersFormatter(code, "en"));
  let { resend } = useLogin();
  let { cellphone } = useSelector(userSelector.login);

  let { back } = props;

  const CELL_COUNT = 5;
  let expireAt = meta.expireAt;
  let currentTime = Date.now();
  let diffCurrentAndExpireTime = dayjs(expireAt).diff(
    dayjs(currentTime),
    "seconds"
  );
  let timeout_id;
  useEffect(() => {
    timeout_id = setTimeout(() => {
      if (diffCurrentAndExpireTime >= 0) {
        setExpireTime(diffCurrentAndExpireTime);
      }
    }, 1000);

    return () => {
      clearTimeout(timeout_id);
    };
  }, [diffCurrentAndExpireTime]);
  const ref = useBlurOnFulfill({ value: code, cellCount: CELL_COUNT });
  const [cell, getCellOnLayoutHandler] = useClearByFocusCell({
    value: code,
    setValue: setCode,
  });
  useEffect(() => {
    ref.current.focus();
  }, []);
  useEffect(() => {
    if (Platform.OS !== "android") {
      ref.current.focus();
    }
  }, [expireTime]);
  let timerVisible = !!expireTime || diffCurrentAndExpireTime > 0;
  let webCodeFieldId = "webVerifyCode";

  return (
    <Box pt="xxl">
      <Box px="l">
        <Stack>
          <Button
            prefix={
              <Icon name="chevron-right" size={20} color="neutrals.750" />
            }
            textColor="neutrals.750"
            fill="transparent"
            onPress={back}
            size="s"
          >
            اصلاح شماره موبایل
          </Button>
        </Stack>
        <Divider size="xxl" />
        <Box px="xl">
          <Typography color="common.black">
            کد ارسال شده به{" "}
            <Typography color="primary.main" ff="mainBold">
              {formatNumber(cellphone)}
            </Typography>{" "}
            را وارد کنید.
          </Typography>
        </Box>
        <Divider size="xxl" />
        <TouchableOpacity
          onPress={() => {
            ref.current.focus();
          }}
        >
          <Stack
            distribution="center"
            direction="vertical"
            style={{ width: "100%" }}
          >
            <Box style={{ width: "100%", maxWidth: 468, position: "relative" }}>
              <WebCodeField id={webCodeFieldId}>
                <CodeField
                  ref={ref}
                  {...cell}
                  value={code}
                  onChangeText={(text: string) => {
                    if (text === "") {
                      setCode("");
                    } else if (isNumeric(text)) {
                      setCode(text);
                    }
                    if (text.length === CELL_COUNT) {
                      onSubmit(text);
                    }
                    return false;
                  }}
                  cellCount={CELL_COUNT}
                  keyboardType="numeric"
                  returnKeyType="send"
                  textContentType="oneTimeCode"
                  {...webInputProps}
                  //@ts-ignore
                  disabled={props.loading}
                  textInputStyle={styles.textInputStyle}
                  renderCell={({ index, symbol, isFocused }) => {
                    let unfocusedInput = !isFocused && !symbol;
                    return (
                      <Typography
                        color={
                          unfocusedInput ? "gray.light960" : "gray.dark200"
                        }
                        key={index}
                        style={[styles.cell, isFocused && styles.focusCell]}
                        onLayout={getCellOnLayoutHandler(index)}
                      >
                        {isFocused ? <Cursor /> : formatNumber(symbol)}
                      </Typography>
                    );
                  }}
                  nativeID={webCodeFieldId}
                />
              </WebCodeField>
            </Box>
          </Stack>
        </TouchableOpacity>

        <Stack pb="xxxl" distribution="center" style={{ position: "relative" }}>
          {props.loading ? (
            <Box style={{ position: "absolute", top: 10 }}>
              <Loading color="primary.main" />
            </Box>
          ) : null}
        </Stack>
        <Divider size="xxxl" />
      </Box>
      <Divider line="neutrals.300" />
      <Box p="l">
        <Stack>
          <Typography color={timerVisible ? "gray.light950" : "common.black"}>
            {timerVisible
              ? "تا درخواست دوباره کد تایید:"
              : "کد تایید را دریافت نکرده اید؟"}
          </Typography>
          <Stack distribution="center">
            {timerVisible ? (
              <>
                <Spacer size="s" />
                <Typography color="common.black" ff="mainBold" fs="xm">
                  {!!expireTime ? formatNumber(formatTime(expireTime)) : ""}
                </Typography>
              </>
            ) : visibleResendCodeButton ? (
              <Button
                textColor="primary.main"
                fill="transparent"
                onPress={() => {
                  resend();
                  setVisibleResendcodeButton(false);
                }}
              >
                فرستادن دوباره کد
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

export default VerifyStep;
